import React from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, Coins, Crown } from "lucide-react";
import ProductCard from "@/components/ProductCard";

const LAYERS = [
  {
    id: "protect",
    label: "Protect",
    subtitle: "Foundational risk transfer — built first.",
    Icon: Shield,
    accent: "from-[#0B2545]/10 to-[#0B2545]/5",
  },
  {
    id: "accumulate",
    label: "Accumulate",
    subtitle: "Tax-advantaged growth that compounds over decades.",
    Icon: TrendingUp,
    accent: "from-[#1E3A8A]/10 to-[#1E3A8A]/5",
  },
  {
    id: "distribute",
    label: "Distribute",
    subtitle: "Lifetime income and healthcare for the retirement years.",
    Icon: Coins,
    accent: "from-[#D4A24C]/15 to-[#D4A24C]/5",
  },
  {
    id: "transfer",
    label: "Transfer",
    subtitle: "Legacy, business continuity, and tax-efficient wealth transfer.",
    Icon: Crown,
    accent: "from-[#0B2545]/15 to-[#D4A24C]/10",
  },
];

/**
 * Renders a list of products grouped by 4 pyramid layers using ProductCard.
 * Each product appears under its `wealth_pyramid_layer`. Designed for the
 * Landing page Solutions section.
 */
export const PyramidLayerGroups = ({ products }) => {
  if (!products || !products.length) return null;
  const grouped = LAYERS.map((layer) => ({
    ...layer,
    products: products.filter((p) => p.wealth_pyramid_layer === layer.id),
  }));
  return (
    <div className="space-y-16">
      {grouped.map((layer) => (
        <div key={layer.id} data-testid={`pyramid-layer-${layer.id}`}>
          <div className="flex items-end justify-between gap-4 mb-6 reveal">
            <div className="flex items-center gap-4 flex-wrap">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br ${layer.accent} text-lw-navy`}
                aria-hidden="true"
              >
                <layer.Icon size={22} />
              </div>
              <div>
                <span className="lw-eyebrow">{`Layer ${LAYERS.findIndex((l) => l.id === layer.id) + 1} — ${layer.label}`}</span>
                <p className="mt-1 text-sm sm:text-base text-lw-muted max-w-xl">
                  {layer.subtitle}
                </p>
              </div>
            </div>
            <Link
              to="/strategy"
              data-testid={`pyramid-strategy-link-${layer.id}`}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-lw-navy/70 hover:text-lw-navy"
            >
              See full strategy <span aria-hidden>→</span>
            </Link>
          </div>
          <div
            data-testid={`pyramid-products-${layer.id}`}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {layer.products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PyramidLayerGroups;
